import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

interface TourContextType {
  startTour: () => void;
  setTourFunction: (tourFn: () => void) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useGlobalTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useGlobalTour must be used within a TourProvider');
  }
  return context;
};

interface TourProviderProps {
  children: ReactNode;
}

export const GlobalTourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [tourFunction, setTourFunction] = useState<(() => void) | null>(null);

  const startTour = useCallback(() => {
    if (tourFunction) {
      tourFunction();
    }
  }, [tourFunction]);

  const setTourFn = useCallback((tourFn: () => void) => {
    setTourFunction(() => tourFn);
  }, []);

  const value = useMemo(
    () => ({ startTour, setTourFunction: setTourFn }),
    [startTour, setTourFn],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
